import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import { type WalletOperationKind, walletMovementsPath } from "./walletOperationLinks";

/** Where the wallets home is: the destination when the operation's wallet cannot be found. */
export const WALLETS_HOME_PATH = "/app/wallets";

/**
 * The page a payin or payout opens in the wallets: the statement of the wallet it moved, on its movement.
 *
 * - The wallet is the organization's one in the operation's currency (a payout's origin currency, which a
 *   cross-currency payout debits), since there is one per currency.
 * - The movement is the operation's newest leg there (`operation_id` filter), so a refused payout opens on the
 *   money that came back.
 * - Each step that fails falls back one level: the wallet's statement without a movement (a backend without the
 *   filter answers 400, or the leg is not posted yet), then the wallets home.
 *
 * Staff pass the organization they are looking at; members are pinned server-side and pass nothing.
 */
export async function resolveWalletOperationDestination(
  kind: WalletOperationKind,
  operationId: string,
  staff: boolean,
): Promise<string> {
  let organizationId: string;
  let currency: string;
  try {
    if (kind === "payins") {
      const { data } = await PaymentsPayinService.getPayin(operationId);
      organizationId = data.organization_id;
      currency = data.currency;
    } else {
      const { data } = await PaymentsPayoutService.getPayout(operationId);
      organizationId = data.organization_id;
      currency = data.source_currency || data.currency;
    }
  } catch {
    return WALLETS_HOME_PATH;
  }

  let walletId: string | undefined;
  try {
    const { data } = await PaymentsWalletsService.list({
      organizationId: staff ? organizationId : undefined,
      currency,
      limit: 20,
    });
    walletId = data.data.find((wallet) => wallet.currency === currency)?.id;
  } catch {
    return WALLETS_HOME_PATH;
  }
  if (!walletId) return WALLETS_HOME_PATH;

  try {
    const { data } = await PaymentsWalletsService.listTransactions(walletId, {
      operation_id: operationId,
      limit: 1,
    });
    return walletMovementsPath(walletId, data.data[0]?.id ?? null);
  } catch {
    return walletMovementsPath(walletId);
  }
}
